import { useState } from "react";
import { useTranslation } from "react-i18next";

import { type CcPackageSummary, isRunTerminal } from "@/api/code-companion";
import BuildOrderPanel from "@/components/agents/code-companion/BuildOrderPanel";
import ChangeSetPicker, {
  defaultChangeSet,
} from "@/components/agents/code-companion/ChangeSetPicker";
import ConductorStrip from "@/components/agents/code-companion/ConductorStrip";
import CorrectionTrail from "@/components/agents/code-companion/CorrectionTrail";
import EscalationPanel from "@/components/agents/code-companion/EscalationPanel";
import FleetMatrix from "@/components/agents/code-companion/FleetMatrix";
import LedgerTail from "@/components/agents/code-companion/LedgerTail";
import NeedsAHuman, { countNeeds } from "@/components/agents/code-companion/NeedsAHuman";
import ObjectInspector from "@/components/agents/code-companion/ObjectInspector";
import ParityPanel from "@/components/agents/code-companion/ParityPanel";
import RunPanel from "@/components/agents/code-companion/RunPanel";
import StartRunButton from "@/components/agents/code-companion/StartRunButton";
import TraceHero from "@/components/agents/code-companion/TraceHero";
import TransportHistory from "@/components/agents/code-companion/TransportHistory";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  useCcChangeSets,
  useCcCorrections,
  useCcEscalations,
  useCcPackageRun,
  useCcReadyGate,
} from "@/hooks/useCodeCompanion";
import { CC_GOLD_INK } from "@/lib/cc-trace-tokens";
import { type CcObjectRef, sameObject } from "@/lib/cc-object-ref";

/**
 * O Trace como **destino**, não como conteúdo de gaveta (S-064 onda 7).
 *
 * A divergência entre o app e o protótipo nunca foi paleta nem fonte — ✅ os 69 tokens do
 * bundle existem idênticos no `index.css`, porque o design system foi extraído do próprio
 * app. Era arquitetura de informação: o protótipo trata Trace como uma tab de primeira
 * classe e o app o tratava como o décimo painel empilhado dentro de um `Sheet` estreito,
 * que só abre clicando num card.
 *
 * A ordem aqui é a do protótipo, e ela tem uma lógica de leitura:
 *
 * 1. **hero** — onde estou (pacote, objetos, change set) e posso promover
 * 2. **faixa do maestro** — quem conduz, o que está rodando, quanto resta de sessão
 * 3. **matriz** — o estado de cada objeto em cada fase
 * 4. **precisa de gente + tail** — o que depende de mim, e o que se moveu agora
 * 5. **o resto** — a narrativa, a ordem, o transporte, a paridade
 *
 * Descendo a página se vai do geral ao específico, e cada bloco responde uma pergunta
 * que o anterior deixou em aberto.
 */

/** Rótulo de bloco, no mesmo tom do "Package trace" do protótipo. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="uppercase"
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: "9px",
        color: CC_GOLD_INK,
        letterSpacing: ".2em",
        fontWeight: 600,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Bloco de historico, fechado por padrao.
 *
 * Trilha de correcao e historico de transporte respondem "o que ja aconteceu", que
 * e pergunta de auditoria, nao de conducao. Abertos, competiam em altura com a
 * matriz e empurravam o resto pra baixo da dobra.
 */
function Fold({ label, children }: { label: string; children: React.ReactNode }) {
  const [aberto, setAberto] = useState(false);
  return (
    <Collapsible open={aberto} onOpenChange={setAberto}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-left text-[11px] font-semibold text-muted-foreground hover:border-primary">
        <span
          className="inline-block transition-transform"
          style={{ transform: aberto ? "rotate(90deg)" : "none" }}
        >
          ›
        </span>
        {label}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}

export default function TraceScreen({
  projectId,
  pkg,
  onOpenEvidence,
}: {
  projectId: string;
  pkg: CcPackageSummary;
  onOpenEvidence?: () => void;
}) {
  const { t } = useTranslation();
  // Nome sozinho não identifica objeto: em pilha RAP `ZI_PEDIDO` é DDLS e BDEF (S-072 F0).
  const [inspected, setInspected] = useState<CcObjectRef | null>(null);

  const run = useCcPackageRun(projectId, pkg.id);
  // CC.34 F2: o run passou a **criar** o change set e a preenchê-lo objeto a objeto.
  // Sem repetir estas duas perguntas enquanto ele roda, a tela ficaria congelada em
  // "este pacote não tem change set" com o worker trabalhando do outro lado, e a única
  // saída seria recarregar a página.
  const runLive = !!run.data && !isRunTerminal(run.data.status);
  const changeSets = useCcChangeSets(projectId, pkg.id, { live: runLive });
  const gate = useCcReadyGate(projectId, pkg.id);

  // O escolhido à mão vence; o padrão é o mais novo COM objeto. Pegar `items[0]` cru
  // abria a matriz vazia quando o change set mais recente não tinha objeto — e o pacote
  // 4 tem dois assim.
  const [chosenChangeSet, setChosenChangeSet] = useState<number | null>(null);
  const changeSetId = chosenChangeSet ?? defaultChangeSet(changeSets.data?.items);

  // Mesmas queries que o painel usa; o react-query desduplica, entao a contagem nao
  // custa requisicao nova. Ela decide onde o painel entra, e nao existe uma segunda
  // regra: `countNeeds` chama o mesmo `collectNeeds` que a fila renderiza.
  const escalations = useCcEscalations(projectId, run.data?.run_id);
  const corrections = useCcCorrections(projectId, changeSetId ?? undefined);
  const needsHumanCount = countNeeds({
    run: run.data ?? null,
    escalations: escalations.data?.items,
    corrections: corrections.data,
    gate: gate.data,
  });

  const needsHumanPanel = (
    <NeedsAHuman
      projectId={projectId}
      packageId={pkg.id}
      changeSetId={changeSetId}
      run={run.data ?? null}
    />
  );

  return (
    <div className="flex flex-col">
      <TraceHero
        pkg={pkg}
        changeSetId={changeSetId}
        gate={gate.data}
        action={
          <StartRunButton
            projectId={projectId}
            packageId={pkg.id}
            changeSetId={changeSetId}
            run={run.data ?? null}
          />
        }
      />
      <ConductorStrip projectId={projectId} run={run.data ?? null} />

      <div className="flex flex-col gap-4 px-6 py-5">
        <div>
          <div
            className="mb-1 uppercase"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "9px",
              color: CC_GOLD_INK,
              letterSpacing: ".2em",
              fontWeight: 600,
            }}
          >
            {t("Package trace")}
          </div>
          {/* Mesma hierarquia da tab Evidence: olho-de-boi, título, parágrafo. Sem o
              título a Trace parece continuação do hero, e não um destino próprio. */}
          <h2
            className="m-0 flex items-center gap-2.5"
            style={{
              fontFamily: "var(--font-brand)",
              fontWeight: 700,
              fontSize: "20px",
              letterSpacing: "-0.015em",
            }}
          >
            {pkg.devclass}
          </h2>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <ChangeSetPicker
              items={changeSets.data?.items ?? []}
              value={changeSetId}
              onChange={setChosenChangeSet}
            />
          </div>
          <p className="mt-1 max-w-[560px] text-[12.5px]" style={{ color: "var(--exed-ink-500)" }}>
            {t(
              "A row is an object. A column is a phase. Down a column: where the package stands. Across a row: what happened to one object.",
            )}
          </p>
        </div>

        {/* Com item na fila, o painel sobe pra cima da matriz e ocupa a largura toda:
            era o bloco que mais exigia acao e ficava abaixo da dobra, no par de baixo,
            com o mesmo peso visual dos outros oito. Sem item, ele volta pro par. */}
        {needsHumanCount > 0 && needsHumanPanel}

        {/* ⚠️ **Só a matriz depende de change set.** Ela e o ObjectInspector leem objetos
            de um change set; todo o resto desta tela é por pacote ou por run. Gatear o
            bloco inteiro nele, que era o que esta tela fazia, escondia o painel do run,
            a escalação e o ledger enquanto o change set estava sendo montado, que é
            justamente quando eles têm mais a dizer. Pior: pacote em que todo objeto
            termina em handoff ou failed nunca ganha change set, então a lista de quem
            precisa de um dev ficava inalcançável pela Trace, para sempre. */}
        {changeSetId === null ? (
          <div className="rounded-xl border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
            {runLive
              ? t("The run is transforming the objects. The matrix fills in as they land.")
              : t("This package has no change set yet, so there is nothing to trace.")}
          </div>
        ) : (
          <>
            <FleetMatrix
              projectId={projectId}
              packageId={pkg.id}
              changeSetId={changeSetId}
              selected={inspected}
              onSelect={(ref) =>
                setInspected((atual) => (sameObject(atual, ref) ? null : ref))
              }
            />

            {inspected && (
              <ObjectInspector
                projectId={projectId}
                packageId={pkg.id}
                changeSetId={changeSetId}
                objectName={inspected.name}
                objectType={inspected.type}
              />
            )}
          </>
        )}

        {/* O par do protótipo: o que espera decisão, e o que acabou de acontecer. */}
        <div className={needsHumanCount > 0 ? "" : "grid gap-4 lg:grid-cols-[1.15fr_1fr]"}>
          {needsHumanCount === 0 && needsHumanPanel}
          <LedgerTail projectId={projectId} packageId={pkg.id} onOpenFull={onOpenEvidence} />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="flex flex-col gap-4">
            <SectionLabel>{t("Autonomous run")}</SectionLabel>
            {run.data ? (
              <RunPanel projectId={projectId} runId={run.data.run_id} />
            ) : (
              <div className="rounded-lg border border-dashed px-3 py-4 text-xs text-muted-foreground">
                {t("This package has never been run.")}
              </div>
            )}
            {run.data && <EscalationPanel projectId={projectId} runId={run.data.run_id} />}
            {/* A trilha é do change set: sem ele não há correção que contar. */}
            {changeSetId !== null && (
              <Fold label={t("Correction trail")}>
                <CorrectionTrail projectId={projectId} changeSetId={changeSetId} />
              </Fold>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <SectionLabel>{t("Delivery shape")}</SectionLabel>
            <BuildOrderPanel projectId={projectId} packageId={pkg.id} />
            <ParityPanel projectId={projectId} packageId={pkg.id} />
            <Fold label={t("Transport history")}>
              <TransportHistory projectId={projectId} packageId={pkg.id} />
            </Fold>
          </div>
        </div>
      </div>
    </div>
  );
}
